import { useToast } from 'hooks/useToast';
import { apiClient } from 'api';
import { useMutation, useQueryClient } from '@tanstack/react-query';

export interface WorkCultureTraitsPayload {
  assessment_id: string;
  quality1: string;
  quality2: string;
  quality3: string;
  workCultureTraits: {
    [key: string]: string | boolean;
  };
}

export interface WorkStylesPayload {
  assessment_id: string;
  work_style1: string;
  work_style1_rating: number;
  work_style2: string;
  work_style2_rating: number;
  work_style3: string;
  work_style3_rating: number;
  work_style4: string;
  work_style4_rating: number;
}

export interface DeleteWorkCultureTraitsPayload {
  assessment_id: string;
  section: 'culture' | 'work_motivators' | 'workplace_quality';
}

export interface DeleteWorkStylesPayload {
  assessment_id: string;
}

/**
 * Hook for submitting work culture traits and work styles
 */
export const useWorkCultureTraitsEdit = (assessmentId: string | null) => {
  const queryClient = useQueryClient();
  const { openToast } = useToast();

  const patchMutation = useMutation({
    mutationFn: async (payload: WorkCultureTraitsPayload | WorkStylesPayload) => {
      // Work styles are disambiguated for the backend with a section=work_style
      // query param; the other trait sections are inferred from the payload shape.
      const isWorkStyles = 'work_style1' in payload;
      const response = await apiClient.patch('/assessment/work-culture-triat/', payload, {
        ...(isWorkStyles ? { params: { section: 'work_style' } } : {}),
      });
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['workCultureTraits', assessmentId] });
      openToast({
        show: true,
        heading: "Edit Successful",
        text: "Work culture traits edited successfully!",
        type: "info",
        timeOut: 5000,
      });
    },
    onError: (err: any) => {
      const errorMessage = err.response?.data?.message || 'Failed to submit work culture traits';
      openToast({
        show: true,
        heading: "Sorry",
        text: errorMessage,
        type: "error",
        timeOut: 5000,
      });
    },
  });

  return {
    submitWorkCultureTraits: patchMutation.mutate,
    isSubmitting: patchMutation.isPending,
  };
};

/**
 * Hook for deleting work culture traits
 */
export const useWorkCultureTraitsDelete = (assessmentId: string | null) => {
  const queryClient = useQueryClient();
  const { openToast } = useToast();

  const deleteMutation = useMutation({
    mutationFn: async (payload: DeleteWorkCultureTraitsPayload) => {
      const { section, ...data } = payload;
      const response = await apiClient.delete('/assessment/work-culture-triat/', {
        data,
        params: { section },
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workCultureTraits', assessmentId] });
      openToast({
        show: true,
        heading: "Delete Successful",
        text: "Work culture traits deleted successfully!",
        type: "info",
        timeOut: 5000,
      });
    },
    onError: (err: any) => {
      const errorMessage = err.response?.data?.message || 'Failed to delete work culture traits';
      openToast({
        show: true,
        heading: "Delete Failed",
        text: errorMessage,
        type: "error",
        timeOut: 5000,
      });
    },
  });

  return {
    deleteWorkCultureTraits: deleteMutation.mutate,
    isDeleting: deleteMutation.isPending,
  };
};

/**
 * Hook for deleting work styles
 */
export const useWorkStylesDelete = (assessmentId: string | null) => {
  const queryClient = useQueryClient();
  const { openToast } = useToast();

  const deleteMutation = useMutation({
    mutationFn: async (payload: DeleteWorkStylesPayload) => {
      const response = await apiClient.delete('/assessment/work-culture-triat/', {
        data: payload,
        params: { section: 'work_style' },
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workStyles', assessmentId] });
      openToast({
        show: true,
        heading: "Delete Successful",
        text: "Work styles deleted successfully!",
        type: "info",
        timeOut: 5000,
      });
    },
    onError: (err: any) => {
      const errorMessage = err.response?.data?.message || 'Failed to delete work styles';
      openToast({
        show: true,
        heading: "Delete Failed",
        text: errorMessage,
        type: "error",
        timeOut: 5000,
      });
    },
  });

  return {
    deleteWorkStyles: deleteMutation.mutate,
    isDeleting: deleteMutation.isPending,
  };
};